import {
  accountBootstrapClient,
  AccountCommandError,
  newAccountBootstrap,
} from './accountEnrollment'
import axios from 'axios'

export interface FirmBootstrap {
  request_id: string
  command_id: string
  bootstrap_secret: string
}
export interface FirmRegistration extends FirmBootstrap {
  firm_name: string
  owner_email: string
  owner_name: string
  tenant_type: 'solo_ca' | 'medium_firm' | 'large_firm'
  plan_code: 'trial' | 'starter' | 'professional' | 'enterprise'
}
export interface FirmProgress {
  request_id: string
  command_id: string
  status: 'awaiting_email' | 'email_verified' | 'mfa_setup' | 'complete' | 'superseded'
  terms_version: string
  registration_status?: string
  tenant_id?: string
  firm_name?: string
  plan_code?: string
}
export interface FirmRequest {
  request_id: string
  command_id: string
  tenant_id: string
  tenant_code: string
  status: string
}
export interface FirmAcceptance {
  request_id: string
  status: 'pending' | 'held'
}
export interface FirmMfa {
  secret: string
  qr_code_url: string
  backup_codes: string[]
}
export function newFirmBootstrap(): FirmBootstrap {
  return { request_id: crypto.randomUUID(), ...newAccountBootstrap() }
}
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
const phases = [
  'awaiting_owner',
  'pending',
  'executing',
  'held',
  'verified',
  'activated',
  'cancelled',
]
async function send<T>(
  path: string,
  body: FirmBootstrap,
  valid: (value: unknown) => value is T
): Promise<T> {
  let value: unknown
  try {
    value = (
      await accountBootstrapClient.post(path, body, {
        headers: { Authorization: undefined, Cookie: undefined, 'X-Service-Token': undefined },
        withCredentials: false,
      })
    ).data
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    throw new AccountCommandError(
      status === 429
        ? 'Please wait before trying again.'
        : status === 410
          ? 'This proof request expired. Resume pending credential setup or request help.'
          : status === 409
            ? 'The request is not ready for this action. Check its status.'
            : status === 401 || status === 403
              ? 'The current proof or authentication could not be accepted.'
              : status === 422 || status === 413
                ? 'Check the request fields and proof format.'
                : 'The result could not be confirmed. Keep this page open and check the request status.',
      !status || status >= 500,
      status
    )
  }
  if (!valid(value))
    throw new AccountCommandError(
      'The response did not match this request. Check its status.',
      true
    )
  return value
}
const progress =
  (b: FirmBootstrap) =>
  (v: unknown): v is FirmProgress =>
    object(v) &&
    v.request_id === b.request_id &&
    v.command_id === b.command_id &&
    ['awaiting_email', 'email_verified', 'mfa_setup', 'complete', 'superseded'].includes(
      String(v.status)
    ) &&
    typeof v.terms_version === 'string' &&
    /^[a-zA-Z0-9_.-]{1,64}$/.test(v.terms_version) &&
    (v.registration_status === undefined || phases.includes(String(v.registration_status))) &&
    (v.tenant_id === undefined || (typeof v.tenant_id === 'string' && uuid.test(v.tenant_id))) &&
    (v.firm_name === undefined ||
      (typeof v.firm_name === 'string' && v.firm_name.length > 0 && v.firm_name.length <= 255)) &&
    (v.plan_code === undefined ||
      ['trial', 'starter', 'professional', 'enterprise'].includes(String(v.plan_code))) &&
    Object.keys(v).every(k =>
      [
        'request_id',
        'command_id',
        'status',
        'terms_version',
        'registration_status',
        'tenant_id',
        'firm_name',
        'plan_code',
      ].includes(k)
    )
export const firmRequestReply =
  (b: FirmBootstrap) =>
  (v: unknown): v is FirmRequest =>
    object(v) &&
    v.request_id === b.request_id &&
    phases.includes(String(v.status)) &&
    v.command_id === b.command_id &&
    typeof v.tenant_id === 'string' &&
    uuid.test(v.tenant_id) &&
    typeof v.tenant_code === 'string' &&
    /^TH-[a-f0-9]{32}$/.test(v.tenant_code) &&
    Object.keys(v).every(k =>
      ['request_id', 'command_id', 'tenant_id', 'tenant_code', 'status'].includes(k)
    )
const mfa = (v: unknown): v is FirmMfa =>
  object(v) &&
  Object.keys(v).length === 3 &&
  typeof v.secret === 'string' &&
  /^[A-Z2-7]{32}$/.test(v.secret) &&
  typeof v.qr_code_url === 'string' &&
  v.qr_code_url.startsWith('otpauth://totp/') &&
  Array.isArray(v.backup_codes) &&
  v.backup_codes.length === 10 &&
  v.backup_codes.every(c => typeof c === 'string' && /^[A-F0-9]{4}-[A-F0-9]{4}$/.test(c))
const path = (operation: string) => '/auth/firm/onboarding/' + operation
export const firmRegistration = {
  create: (body: FirmRegistration) => send('/tenants', body, firmRequestReply(body)),
  status: (body: FirmBootstrap) => send(path('status'), body, progress(body)),
  verify: (body: FirmBootstrap & { email_proof: string }) =>
    send(path('verify-email'), body, progress(body)),
  resend: (body: FirmBootstrap) => send(path('resend'), body, progress(body)),
  resume: (body: FirmBootstrap & { email: string; previous_command_id: string }) =>
    send(path('resume'), body, progress(body)),
  setup: (
    body: FirmBootstrap & {
      email_proof: string
      username: string
      password: string
      full_name: string
    }
  ) => send(path('mfa-setup'), body, mfa),
  finish: (body: FirmBootstrap & { mfa_code: string; accepted: true; terms_version: string }) =>
    send(path('finish'), body, progress(body)),
  accept: (body: FirmBootstrap & { password: string; mfa_code: string }) =>
    send(
      path('accept'),
      body,
      (v: unknown): v is FirmAcceptance =>
        object(v) &&
        Object.keys(v).length === 2 &&
        v.request_id === body.request_id &&
        (v.status === 'pending' || v.status === 'held')
    ),
  activate: (body: FirmBootstrap & { password: string; mfa_code: string }) =>
    send(path('activate'), body, firmRequestReply(body)),
}
